import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MeritPromotionState } from '@coin/shared/merit-incentive/util';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import {
  BudgetAllocationSettings,
  Employee,
  MercerData,
  MeritAllocationSeason,
  PaginatedResult,
  SearchItemService
} from '@coin/shared/core/util-models';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { FilterSortState } from '@coin/customer/shared/filter-sort-bar-data-access';
import { MeritBudgetAllocationState } from '../../merit-budget-allocation/store/merit-budget-allocation.state';
import { MeritBudgetResponsibilityType } from '../enums/merit-budget-responsibility-type.enum';
import { IncentiveAllocationEmployee } from '../models/incentive-allocation-employee.model';
import { MeritAllocationSnapshot, MeritEmployeeSnapshotSlim } from '../models/merit-allocation-snapshot.model';
import { MeritBudgetCompensationInfo } from '../models/merit-budget-compensation-info.model';
import { EmployeeWithCompensationInfo } from '../models/merit-budget-direct-with-compensation.model';
import { UpdateAllocationValuesDto } from '../models/update-allocation-values-dto.model';
import { MeritAllocationSnapshotMapOperations } from '../operations/merit-allocation-snapshot-map.operations';
import { AllocationOrgDetailsService } from './allocation-org-details-mapping.service';
import { MeritAllocationImpersonatedEmployeeService } from './merit-allocation-impersonated-employee.service';
import { UpdateAllocationValuesService } from './update-allocation-values.service';

const DEFAULT_PAGE_SIZE = 100_000;

@Injectable({ providedIn: 'root' })
export class MeritAllocationGeneralService implements AllocationOrgDetailsService, SearchItemService {
  private readonly httpClient = inject(HttpClient);
  private readonly httpHelpersService = inject(HttpHelpersService);
  private readonly loadingService = inject(LoadingService);
  private readonly updateAllocationValuesService = inject(UpdateAllocationValuesService);
  private readonly toast = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly meritAllocationImpersonatedEmployeeService = inject(MeritAllocationImpersonatedEmployeeService);
  private readonly filterSortState = inject(FilterSortState);
  private readonly injector = inject(Injector);

  public selectedSeasonIdForSearch: string;

  getTotalAllocations(
    page: number,
    size: number,
    seasonId: string,
    filterText = ''
  ): Observable<PaginatedResult<EmployeeWithCompensationInfo>> {
    this.loadingService.present();
    return this.httpClient
      .get<
        PaginatedResult<MeritAllocationSnapshot>
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/total?page=${page}&size=${size}${filterText}${this.meritAllocationImpersonatedEmployeeService.getEmployeeIdQuery()}`)
      .pipe(
        map(data => ({ ...data, content: MeritAllocationSnapshotMapOperations.mapSnapshotsToEmployees(data.content) })),
        this.httpHelpersService.handleError('Cannot get allocation members'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  getAllocationMember(seasonId: string, employeeId: string, orgCode?: string): Observable<MeritBudgetCompensationInfo> {
    const separator = this.meritAllocationImpersonatedEmployeeService.employeeId ? '&' : '?';
    const query = orgCode ? `${separator}orgCode=${encodeURIComponent(orgCode)}` : '';
    return this.httpClient
      .get<MeritAllocationSnapshot>(
        `${
          environment.api.baseUrl
        }/admin/api/v2/master/seasons/merit/${seasonId}/allocations/total/${employeeId}${this.meritAllocationImpersonatedEmployeeService.getEmployeeIdQuery('?')}${query}`
      )
      .pipe(
        map(info => ({
          ...info,
          compensationComponents: MeritAllocationSnapshotMapOperations.mapCompensationComponents(
            info.compensationComponents
          )
        })),
        catchError(() => of(null))
      );
  }

  getEmployeeSnapshotById(seasonId: string, employeeId: string): Observable<MeritEmployeeSnapshotSlim> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .get<MeritEmployeeSnapshotSlim>(
          `${environment.api.baseUrl}/admin/api/v1/merit/seasons/${seasonId}/snapshots/${employeeId}`
        )
        .pipe(this.httpHelpersService.handleError('Cannot get snapshot'))
    );
  }

  getSearchItemsByText(text: string, page: number): Observable<Employee[]> {
    const filterText = this.filterSortState.getFilterText();
    return this.httpClient
      .get<
        PaginatedResult<Employee>
      >(`${environment.api.baseUrl}/admin/api/v1/merit/snapshots/employees?page=${page + 1}&size=50&search=${encodeURIComponent(text)}${this.router.url.includes('list-view') ? '&directs=true' : ''}&meritSeasonId=${this.selectedSeasonIdForSearch}${this.meritAllocationImpersonatedEmployeeService.getEmployeeIdQuery()}${filterText}`)
      .pipe(
        map(res => res.content),
        this.httpHelpersService.handleError('Cannot get employees by search text')
      );
  }

  getMeritPartnerSeasons(): Observable<MeritAllocationSeason[]> {
    this.loadingService.present();
    return this.httpClient
      .get<
        PaginatedResult<MeritAllocationSeason>
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/partner/seasons?page=1&size=${DEFAULT_PAGE_SIZE}`)
      .pipe(
        map(data => data.content),
        this.httpHelpersService.handleError('Cannot get merit seasons'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  getMeritAllocationSeasons(): Observable<MeritAllocationSeason[]> {
    this.loadingService.present();
    return this.httpClient
      .get<
        PaginatedResult<MeritAllocationSeason>
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/allocations?page=1&size=${DEFAULT_PAGE_SIZE}`)
      .pipe(
        map(data => data.content),
        this.httpHelpersService.handleError('Cannot get merit seasons'),
        finalize(() => this.loadingService.dismiss())
      );
  }
  getMeritSeasonSettings(seasonId: string): Observable<BudgetAllocationSettings> {
    this.loadingService.present();
    return this.httpClient
      .get<BudgetAllocationSettings>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/${seasonId}/merit-allocation-settings`
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot load budget allocation settings'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  // employeeId is really employeeId and not allocation member id
  updateDirect(
    seasonId: string,
    employeeId: string,
    body: UpdateAllocationValuesDto
  ): Observable<UpdateAllocationValuesDto> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .put<UpdateAllocationValuesDto>(
          `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/values/${employeeId}`,
          body
        )
        .pipe(this.httpHelpersService.handleError('Cannot update values'))
    );
  }

  public promoteEmployee(
    seasonId: string,
    employeeId: string,
    { comment, mercerData }: { comment: string; mercerData?: MercerData }
  ): Observable<void> {
    this.loadingService.present();
    return this.httpClient
      .patch<void>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${employeeId}/promote`,
        {
          state: MeritPromotionState.Requested,
          comment,
          mercerData: mercerData ? { ...mercerData, jobCodeId: mercerData.id } : undefined
        }
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot promote employee'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  undoEmployeePromotion(seasonId: string, employeeId: string): Observable<void> {
    this.loadingService.present();
    return this.httpClient
      .patch<void>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${employeeId}/reset-promotion`,
        {
          seasonId,
          employeeId
        }
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot undo promotion'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  approveEmployee(seasonId: string, employeeId: string): Observable<void> {
    this.loadingService.present();
    return this.httpClient
      .patch<void>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${employeeId}/approve`,
        {}
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot approve employee'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  getFieldValues(
    page: number,
    size: number,
    key: string,
    searchText: string,
    seasonId: string,
    parentOrgCode: string
  ): Observable<PaginatedResult<string>> {
    let searchQuery = searchText?.trim() ? `&${key}=${searchText}` : '';
    // read lazily: MeritBudgetAllocationState injects this service (NG0200)
    const responsibility = this.injector.get(MeritBudgetAllocationState).responsibilityType();
    const isDirect = responsibility === MeritBudgetResponsibilityType.DirectResponsibility;
    const pathSegment = isDirect ? 'directs' : 'totals';
    if (!isDirect) {
      searchQuery += `&ParentOrgCode=${encodeURIComponent(parentOrgCode)}`;
    }

    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${pathSegment}/field-values?Page=${page + 1}&Size=${size}&targetProperty=${key}${searchQuery}${this.meritAllocationImpersonatedEmployeeService.getEmployeeIdQuery()}`)
      .pipe(catchError(() => of(null)));
  }

  updateDirects(
    directs: EmployeeWithCompensationInfo[],
    updateBody: Partial<EmployeeWithCompensationInfo>,
    seasonId: string
  ): Observable<UpdateAllocationValuesDto[]> {
    const requests$ = directs.map(direct => {
      const body = this.updateAllocationValuesService.getIsDoneOnlyUpdateBody({ ...direct, ...updateBody });
      return this.updateDirect(seasonId, direct.employeeId, body);
    });

    this.loadingService.present();
    return forkJoin(requests$).pipe(
      tap(() => this.toast.success(`${directs.length} items updated.`)),
      finalize(() => this.loadingService.dismiss())
    );
  }

  flagMeritAllocation(
    seasonId: string,
    allocationMemberId: string,
    isFlagged: boolean
  ): Observable<EmployeeWithCompensationInfo | IncentiveAllocationEmployee> {
    this.loadingService.present();
    return this.httpClient
      .put<
        EmployeeWithCompensationInfo | IncentiveAllocationEmployee
      >(`${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${allocationMemberId}/flag`, { isFlagged })
      .pipe(
        this.httpHelpersService.handleError('Cannot flag employee'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public getAllocationSettings(seasonId: string): Observable<BudgetAllocationSettings> {
    this.loadingService.present();
    return this.httpClient
      .get<BudgetAllocationSettings>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/${seasonId}/merit-allocation-settings`
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot load budget allocation settings'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public getPromotionApprover(employeeId: string, seasonId: string) {
    return this.httpClient
      .get<{
        promotionApproverType: string;
        promotionApproverDetails: string;
      }>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${employeeId}/promotion-approver`
      )
      .pipe(this.httpHelpersService.handleError('Cannot get promotion approver'));
  }

  public setWasPromoted(seasonId: string, employeeId: string, wasPromoted: boolean) {
    this.loadingService.present();
    return this.httpClient
      .patch<void>(
        `${environment.api.baseUrl}/admin/api/v2/master/seasons/merit/${seasonId}/allocations/${employeeId}/was-promoted`,
        { wasPromoted }
      )
      .pipe(
        this.httpHelpersService.handleError('Cannot update promotion state'),
        finalize(() => this.loadingService.dismiss())
      );
  }
}
